import { useState, useEffect } from "react";
import {
  TransactionDueDateModel,
  ValidationErrorModel,
  RowStateEnum,
} from "../../models";
import {
  getTransactionDueDateById,
  saveTransactionDueDate,
} from "../../serviceBroker/transactionDueDateServiceBroker";
import { useSearchParams } from "react-router-dom";
import { formatToIsoString } from "../../utils";

export function useFormActions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id") || "";
  const initialValues: TransactionDueDateModel = {
    Transaction_Code: "",
    Amount_Due: 0,
    DueDate: new Date().toISOString(),
    Transaction_ID: 0,
    AC_Trasnaction_ID: null,
    Customer_ID: 0,
    Amount_Paid: 0,
    Remarks: "",
    Paid_Date: null,
    PaidBy: null,
    AC_Transaction_Code: null,
    ID: 0,
    CreatedBy: 0,
    ModifiedBy: null,
    CusomerNameAr: null,
    CusomerNameEn: null,
    CreationDate: "",
    ModificationDate: null,
    rowState: Number(RowStateEnum.Update),
  };

  const [state, setState] = useState<{
    data: TransactionDueDateModel;
    loading: boolean;
    errors: ValidationErrorModel[];
  }>({
    data: initialValues,
    loading: false,
    errors: [],
  });

  // Fetch data when id changes
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setState((prev) => ({
          ...prev,
          data: initialValues,
          loading: false,
        }));
        return;
      }

      setState((prev) => ({ ...prev, loading: true, errors: [] }));

      try {
        const decodedId = atob(id); // Base64 decode
        const response = await getTransactionDueDateById(Number(decodedId));
        if (response.Errors?.length) {
          setState((prev) => ({
            ...prev,
            loading: false,
            errors: response.Errors || [],
          }));
        } else {
          setState((prev) => ({
            ...prev,
            data: response.Result || initialValues,
            loading: false,
          }));
        }
      } catch (err: any) {
        const errorMessage =
          typeof err === "string" ? err : "Error fetching data";
        setState((prev) => ({
          ...prev,
          loading: false,
          errors: [{ MessageAr: errorMessage, MessageEn: errorMessage }],
        }));
      }
    };

    fetchData();
  }, [id]);

  // Save data function
  const saveData = async (
    request: TransactionDueDateModel
  ): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, errors: [] }));

    try {
      request.rowState = Number(RowStateEnum.Update);
      request.DueDate = formatToIsoString(request.DueDate);
      const res = await saveTransactionDueDate(request);

      if (res?.Errors?.length) {
        setState((prev) => ({
          ...prev,
          loading: false,
          errors: res.Errors || [],
        }));
        return false;
      }

      setState((prev) => ({
        ...prev,
        data: res.Result || request,
        loading: false,
        errors: [],
      }));

      setSearchParams(new URLSearchParams());
      return true;
    } catch (err: any) {
      const errorMessage = typeof err === "string" ? err : "Error saving data";
      setState((prev) => ({
        ...prev,
        loading: false,
        errors: [{ MessageAr: errorMessage, MessageEn: errorMessage }],
      }));
      return false;
    }
  };

  // Reset errors function
  const clearErrorsAndId = () => {
    setState((prev) => ({ ...prev, errors: [] }));
    setSearchParams(new URLSearchParams());
  };

  return {
    data: state.data,
    loading: state.loading,
    errors: state.errors,
    saveData,
    clearErrorsAndId,
  };
}
